// import serverUrl from '../assets/icon/orlando.svg';

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef
} from 'react';
import { fetchLatestVideo } from '../services/youtubeService';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'default';
export type ToastPosition =
  | 'top-left'
  | 'top-right'
  | 'top-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: ReactNode;
  thumbnail?: string;
  href?: string;
  createdAt: number;
  position: ToastPosition;
  duration: number;
  persistent?: boolean;
}

interface ToastContextProps {
  toasts: Toast[];
  notify: (
    type: ToastType,
    title: string,
    message: ReactNode,
    position?: ToastPosition,
    duration?: number,
    persistent?: boolean,
    thumbnail?: string,
    href?: string
  ) => void;
  removeToast: (id: string) => void;
  position: ToastPosition;
  setPosition: (position: ToastPosition) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [position, setPosition] = useState<ToastPosition>('bottom-right');
  const initialToastShown = useRef(false);

  const removeToast = useCallback((id: string) => {
    setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id));
  }, []);

  const notify = useCallback(
    (
      type: ToastType,
      title: string,
      message: ReactNode,
      position?: ToastPosition,
      duration: number = 5000,
      persistent: boolean = false,
      thumbnail?: string,
      href?: string
    ) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      const newToast: Toast = {
        id,
        type,
        title,
        message,
        thumbnail,
        href,
        createdAt: Date.now(),
        position: position || 'bottom-right',
        duration,
        persistent
      };

      setToasts(currentToasts => [newToast, ...currentToasts]);

      if (!persistent) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  useEffect(() => {
    if (initialToastShown.current) return;
    initialToastShown.current = true;

    // notify(
    //   'default',
    //   'Участие в рубеже',
    //   <>
    //     Все сервера Маджестик РП, кроме{' '}
    //     <img
    //       src={serverUrl}
    //       alt="Orlando"
    //       style={{
    //         width: 16,
    //         height: 16,
    //         display: 'inline',
    //         verticalAlign: 'middle',
    //         margin: '0 2px'
    //       }}
    //     />{' '}
    //     Orlando
    //   </>,
    //   'bottom-left',
    //   5000,
    //   true
    // );

    void fetchLatestVideo().then(video => {
      if (!video) return;
      notify(
        'default',
        'Новое видео на канале',
        video.title,
        'bottom-left',
        5000,
        true,
        video.thumbnail,
        video.url
      );
    });

    return () => {
      initialToastShown.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToastContext.Provider
      value={{ toasts, notify, removeToast, position, setPosition }}
    >
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
